import { api } from "../Components/functions/Apiwithouttoken";
import { nibiruApi } from '../Components/functions/NibiruApi';

class PropertyService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.performanceMetrics = {
      totalRequests: 0,
      cacheHits: 0,
      averageResponseTime: 0,
    };
  }

  /**
   * Pre-load properties for better user experience
   * This should be called on the main onboarding page
   */
  async preloadProperties(dispatch) {
    const cacheKey = 'preload_properties';
    
    // Check if we already have a pending request
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    const startTime = Date.now();
    dispatch({ type: 'rentalProperties/startPreloading' });

    const requestPromise = this._fetchProperties()
      .then((properties) => {
        const timestamp = Date.now();
        const responseTime = timestamp - startTime;
        
        // Update performance metrics
        this.performanceMetrics.totalRequests++;
        this.performanceMetrics.averageResponseTime = 
          (this.performanceMetrics.averageResponseTime + responseTime) / 2;

        // Cache the results
        this.cache.set(cacheKey, {
          data: properties,
          timestamp,
          expiry: timestamp + (5 * 60 * 1000), // 5 minutes
        });

        dispatch({
          type: 'rentalProperties/preloadPropertiesSuccess',
          payload: { properties, timestamp }
        });

        // Pre-load critical property data
        this._preloadCriticalProperties(properties.slice(0, 6));

        return properties;
      })
      .catch((error) => {
        console.error('Preload properties failed:', error);
        dispatch({
          type: 'rentalProperties/preloadPropertiesFailure',
          payload: error.message
        });
        throw error;
      })
      .finally(() => {
        this.pendingRequests.delete(cacheKey);
      });

    this.pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }

  /**
   * Load properties with caching and smart refresh logic
   */
  async loadProperties(dispatch, options = {}) {
    const {
      forceRefresh = false,
      isRefresh = false,
      useCache = true,
    } = options;

    const cacheKey = 'properties';
    const cached = this.cache.get(cacheKey);
    
    // Use cache if available and not expired
    if (useCache && cached && !forceRefresh && Date.now() < cached.expiry) {
      this.performanceMetrics.cacheHits++;
      console.log('Using cached properties');
      
      dispatch({
        type: 'rentalProperties/loadPropertiesSuccess',
        payload: { 
          properties: cached.data, 
          timestamp: cached.timestamp,
          isRefresh 
        }
      });
      
      return cached.data;
    }

    // Check for pending request
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    const startTime = Date.now();
    dispatch({ type: 'rentalProperties/startLoading' });

    const requestPromise = this._fetchProperties()
      .then((properties) => {
        const timestamp = Date.now();
        const responseTime = timestamp - startTime;
        
        // Update performance metrics
        this.performanceMetrics.totalRequests++;
        this.performanceMetrics.averageResponseTime = 
          (this.performanceMetrics.averageResponseTime + responseTime) / 2;

        // Cache the results
        this.cache.set(cacheKey, {
          data: properties,
          timestamp,
          expiry: timestamp + (5 * 60 * 1000), // 5 minutes
        });

        dispatch({
          type: 'rentalProperties/loadPropertiesSuccess',
          payload: { properties, timestamp, isRefresh }
        });

        return properties;
      })
      .catch((error) => {
        console.error('Load properties failed:', error);
        dispatch({
          type: 'rentalProperties/loadPropertiesFailure',
          payload: error.message
        });
        throw error;
      })
      .finally(() => {
        this.pendingRequests.delete(cacheKey);
      });

    this.pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }

  /**
   * Refresh properties (force reload)
   */
  async refreshProperties(dispatch) {
    dispatch({ type: 'rentalProperties/refreshProperties' });
    return this.loadProperties(dispatch, { forceRefresh: true, isRefresh: true });
  }

  /**
   * Get cached properties if available
   */
  getCachedProperties() {
    const cached = this.cache.get('properties') || this.cache.get('preload_properties');
    if (cached && Date.now() < cached.expiry) {
      return cached.data;
    }
    return null;
  }

  /**
   * Check if properties are cached and valid
   */
  isPropertiesCached() {
    const cached = this.cache.get('properties') || this.cache.get('preload_properties');
    return cached && Date.now() < cached.expiry;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics() {
    return {
      ...this.performanceMetrics,
      cacheHitRate: this.performanceMetrics.totalRequests > 0 
        ? (this.performanceMetrics.cacheHits / this.performanceMetrics.totalRequests) * 100 
        : 0,
    };
  }

  /**
   * Private method to fetch properties from API
   */
  async _fetchProperties() {
    try {
      const res = await api("property/getProperties", {});
      const propertyArray = Array.isArray(res) ? res : [];
      
      console.log(`PropertyService - Fetched ${propertyArray.length} properties from API`);
      
      return propertyArray;
    } catch (error) {
      console.error('PropertyService - API call failed:', error);
      throw error;
    }
  }

  /**
   * Pre-load critical property data for better performance
   */
  async _preloadCriticalProperties(properties) {
    if (properties.length === 0) return;

    try {
      const criticalTokens = properties.map(p => p.token_id);
      await nibiruApi.batchQueryProperties(criticalTokens, 'high');
      console.log('PropertyService - Critical properties pre-loaded');
    } catch (error) {
      console.warn('PropertyService - Failed to pre-load critical properties:', error);
    }
  }

  /**
   * Smart property filtering
   */
  filterProperties(properties, filters = {}) {
    if (!Array.isArray(properties)) return [];

    return properties.filter(property => {
      // Basic filter: only show listed properties
      if (!property?.short?.islisted) return false;

      // Apply additional filters if provided
      if (filters.priceRange) {
        const price = property.short?.price_per_day || 0;
        if (price < filters.priceRange.min || price > filters.priceRange.max) {
          return false;
        }
      }

      if (filters.amenities && Object.keys(filters.amenities).length > 0) {
        const propertyAmenities = property.metaData?.amenities || {};
        for (const [amenity, required] of Object.entries(filters.amenities)) {
          if (required && !propertyAmenities[amenity]) {
            return false;
          }
        }
      }

      if (filters.propertyType && filters.propertyType !== 'all') {
        const propertyType = property.metaData?.propertyType || 'apartment';
        if (propertyType !== filters.propertyType) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * Sort properties
   */
  sortProperties(properties, sortType = 'default') {
    if (!Array.isArray(properties)) return [];

    const sorted = [...properties];

    switch (sortType) {
      case 'lowToHigh':
        return sorted.sort((a, b) => (a.short?.price_per_day || 0) - (b.short?.price_per_day || 0));
      case 'highToLow':
        return sorted.sort((a, b) => (b.short?.price_per_day || 0) - (a.short?.price_per_day || 0));
      case 'rating':
        return sorted.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
      case 'newest':
        return sorted.sort((a, b) => (b.token_id || 0) - (a.token_id || 0));
      default:
        return sorted;
    }
  }
}

// Create singleton instance
const propertyService = new PropertyService();

export default propertyService;
